import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { mkdtemp, readFile, writeFile, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

/** Launcher for Windsurf — Codeium's AI coding IDE/cli */
export class WindsurfLauncher implements ProviderLauncher {
  readonly name = 'windsurf'
  readonly displayName = 'Windsurf'
  readonly pricingHint = '🆓 Tier gratuit disponible (Codeium)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('windsurf', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable('@codeium/windsurf')
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('windsurf', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('windsurf', ['--version'])
      return 'windsurf'
    } catch {
      return 'npx'
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    // Write system prompt to a temp dir the candidate can't access
    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-windsurf-'))
    await writeFile(join(agentDir, '.windsurfrules'), systemPrompt)

    const args: string[] = []

    if (config.model) {
      args.push('--model', config.model)
    }

    args.push(workdir)

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? ['@codeium/windsurf', ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }
}
